interface InventoryApiPayload {
  category_options?: unknown;
  condition_options?: unknown;
  groups?: unknown;
  status_options?: unknown;
}

/** Laravel JSON may nest under `data`; options may use `name` instead of `value`. */
function hasInventoryGroups(groups: unknown): boolean {
  const isGroupsArray = Array.isArray(groups);
  const isGroupsRecord = groups != null && typeof groups === 'object';
  return isGroupsArray || isGroupsRecord;
}

function extractInventoryPayload(response: { data?: unknown }): InventoryApiPayload | null {
  const root = response.data as Record<string, unknown> | undefined;
  if (!root) return null;

  const inner = root.data as InventoryApiPayload | undefined;
  const innerHasInventoryPayload = inner != null && hasInventoryGroups(inner.groups);
  if (innerHasInventoryPayload) return inner;

  const rootHasInventoryPayload = hasInventoryGroups(root.groups);
  if (rootHasInventoryPayload) return root as InventoryApiPayload;

  return null;
}

/**
 * Combine the human label (e.g. `CAT-1`) with the immutable serial number (e.g. `1`) for inline
 * display. Used everywhere we previously showed `equipment_label` alone — checkout summaries,
 * end-rental modal, toasts, etc. — so staff always see both identifiers without having to
 * cross-reference the inventory grid.
 *
 *  - both present     →  `"CAT-1 — 1"`
 *  - label only       →  `"CAT-1"`
 *  - serial only      →  `"1"` (very old units that pre-date the label)
 *  - neither present  →  `""`
 */
function formatEquipmentLabelWithSerial(label?: string | null, serial?: string | null): string {
  const trimmedLabel = String(label ?? '').trim();
  const trimmedSerial = String(serial ?? '').trim();
  if (trimmedLabel && trimmedSerial) return `${trimmedLabel} — ${trimmedSerial}`;
  return trimmedLabel || trimmedSerial;
}

export { extractInventoryPayload, formatEquipmentLabelWithSerial };
export type { InventoryApiPayload };
